/**
 * <SlotChangeFallbackStep> — l'étape « Si Camille ne peut pas ? » de « Modifier mes créneaux »
 * (organism).
 *
 * Un tuteur à qui l'on propose de nouveaux horaires garde aussi un autre cours de l'enfant. Ses
 * horaires dépendent de sa confirmation : s'il ne peut pas, la famille dit d'avance ce qu'elle
 * veut — le garder sur son autre créneau et prendre un autre tuteur pour le nouvel horaire, ou
 * prendre un autre tuteur pour tous ses créneaux de la matière (recommandé). Rien n'est coché
 * d'avance : la famille choisit, « Continuer » attend un choix par tuteur. Un bloc par tuteur
 * concerné : sa photo en tête de la question, puis les deux choix en radio, l'étiquette
 * « Recommandé » posée sur le second (maquettes B5 et D5 du shaping).
 * Un tuteur dont les cours changent sans qu'il en garde aucun n'a rien à choisir : une phrase le
 * dit, sous les blocs (« Ceux de Yanis aussi. S'il ne peut pas, nous cherchons un autre tuteur
 * pour son cours de français. »), avec sa matière quand ce tuteur a déjà sa question pour une autre
 * (« Ceux de Camille en français aussi. »). Plusieurs matières : chaque question nomme la sienne.
 *
 * Contrôlé : les choix arrivent (`choices`, repli → valeur), un choix repart par `onSelect`.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Avatar } from "../../atoms/Avatar"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { OptionCard } from "../../molecules/OptionCard"
import {
    type UISlotChangeCourse,
    type UISlotChangeFallback,
    type UISlotChangeFallbackChoice,
    type UITutor,
} from "../../../ports/primary/ui/types"
import { tutorShortName } from "../../../ports/primary/ui/formatters/liveSessionFormatter"
import {
    anotherTutorFor,
    fallbackSubjectSuffix,
    ifTutorCannot,
    keepTutorLabel,
    otherTutorLabel,
    theirsTooLabel,
    tutorsWithoutFallback,
} from "../../../ports/primary/ui/formatters/slotChangeFormatter"

const AVATAR_SIZE = 40

export type SlotChangeFallbackStepProps = {
    fallbacks: UISlotChangeFallback[]
    /** Les cours qui changent — ce qui compte les créneaux d'un tuteur. */
    courses: UISlotChangeCourse[]
    choices: Record<string, UISlotChangeFallbackChoice>
    onSelect: (key: string, choice: UISlotChangeFallbackChoice) => void
    testID?: string
}

export function SlotChangeFallbackStep(props: SlotChangeFallbackStepProps) {
    const { fallbacks, courses, choices, onSelect, testID } = props
    const others = tutorsWithoutFallback(fallbacks, courses)
    const several = fallbacks.length + others.length > 1

    return (
        <Stack gap="5" testID={testID}>
            {fallbacks.map((fallback) => {
                const name = fallback.tutor.firstname || "son tuteur"
                const choice = choices[fallback.key]
                return (
                    <Stack key={fallback.key} gap="3">
                        <TutorLine tutor={fallback.tutor}>
                            {`Les horaires de ${name} dépendent de sa confirmation. `}
                            <Text size="text-sm" weight="bold" color="text-strong">
                                {`${ifTutorCannot(fallback.tutor)}${fallbackSubjectSuffix(fallback, several)} :`}
                            </Text>
                        </TutorLine>
                        <OptionCard
                            label={keepTutorLabel(fallback)}
                            control="radio"
                            selected={choice === "keep_tutor"}
                            onPress={() => onSelect(fallback.key, "keep_tutor")}
                        />
                        <OptionCard
                            label={otherTutorLabel(fallback, courses)}
                            control="radio"
                            tag="Recommandé"
                            selected={choice === "other_tutor"}
                            onPress={() => onSelect(fallback.key, "other_tutor")}
                        />
                    </Stack>
                )
            })}
            {others.map((other) => (
                <TutorLine key={other.key} tutor={other.tutor}>
                    {`${theirsTooLabel(other)} `}
                    <Text size="text-sm" weight="bold" color="text-strong">
                        {ifTutorCannot(other.tutor)}
                    </Text>
                    {`, ${anotherTutorFor(other)}`}
                </TutorLine>
            ))}
        </Stack>
    )
}

/** La photo du tuteur en tête d'une phrase qui parle de lui. */
function TutorLine(props: { tutor: UITutor; children: React.ReactNode }) {
    const { tutor, children } = props
    return (
        <Row gap="3" align="center">
            <Avatar uri={tutor.avatarUrl} name={tutorShortName(tutor)} size={AVATAR_SIZE} signed />
            <Text size="text-sm" color="text-strong" style={styles.flex}>
                {children}
            </Text>
        </Row>
    )
}

const styles = StyleSheet.create(() => ({
    flex: { flexShrink: 1 },
}))
